import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import * as yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { Container, FormControlLabel, Grid, Radio } from "@mui/material";
import { Form } from "react-final-form";
import { CloseIcon } from "../../../../styles/img/svg/CloseIcon";
import { BackIcon } from "../../../../styles/img/svg/BackIcon";
import { ProgressBar } from "../../../_uiComponents/Onboarding/ProgressBar";
import {
  clearValidateUsernameData,
  onboardingClearFormResponseData,
  onboardingUsernameValidateRequest,
  removeFormDataFeilds,
  storeProfileWizardDetails,
} from "../../../../_redux/_actions/onboarding.actions";
import { clearCommonErrors } from "../../../../_redux/_actions";
import {
  commonConstants,
  onboardingConstants,
  responseCodes,
} from "../../../../_redux/_constants";
import Validator from "../../../../_helpers/validate.schema";
import UiInput from "../../../_uiComponents/UiInput";
import { TickGreenIcon } from "../../../../styles/img/svg/TickGreenIcon";
import { AlertIcon } from "../../../../styles/img/svg/AlertIcon";
import { UiButton } from "../../../_uiComponents/common/UiButton";
import UiRadioInput from "../../../_uiComponents/common/UiRadioInput";
import { ClearIcon } from "../../../../styles/img/svg/ClearIcon";

export const UsernameForm = (props) => {
  const {
    ownFormData: ownFormData,
    userNameValidateData: userNameValidateData,
    otpConfirmationData,
  } = useSelector((state) => state.onboarding);
  const [isUserNameValid, setIUserNameValid] = useState(
    !!ownFormData?.username,
  );
  const [hasNameSuggestions, setHasNameSuggestions] = useState(false);
  const [validUsername, setValidUsername] = useState("");

  const [commonErrorMsg, setCommonErrorMsg] = useState("");

  const [selectedUsername, setSelectedUsername] = useState(
    ownFormData?.username ? ownFormData?.username : "",
  );

  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();

  const validateSchema = () =>
    yup.object().shape({
      username: yup
        .string()
        .matches(
          /^(?=(?:.*\d){4,})(?=.*[a-zA-Z])[a-zA-Z0-9]{6,20}$/,
          "Only alphanumeric characters are allowed",
        )
        .min(6, "Username must be at least 6 characters")
        .required(t("This field is required")),
    });

  const onValidate = async (values) => {
    return await Validator(values, validateSchema);
  };

  const goBack = () => {
    if (
      otpConfirmationData?.isSubProfileActivationProcessCompleted ==
      commonConstants.YES_OPTION_TYPE
    ) {
      dispatch(onboardingClearFormResponseData());
      dispatch(clearCommonErrors());

      props?.changeFlowStep(onboardingConstants.NIC_PASSPORT_VALIDATION_STEP);
    }

    dispatch(onboardingClearFormResponseData());
    dispatch(clearCommonErrors());

    switch (ownFormData?.registrationType) {
      case onboardingConstants.REGISTRATION_OPTION_DEBIT_CARD:
        props?.changeFlowStep(
          onboardingConstants.REGISTRATION_OPTION_DEBIT_CARD,
        );
        break;

      case onboardingConstants.REGISTRATION_OPTION_CASA_ACCOUNT:
        if (
          ownFormData?.alternativeFLowActive &&
          ownFormData?.alternativeFLowActive == commonConstants.YES_OPTION_TYPE
        ) {
          props?.changeFlowStep(
            onboardingConstants.NIC_PASSPORT_VALIDATION_STEP,
          );
        } else {
          props?.changeFlowStep(
            onboardingConstants.REGISTRATION_OPTION_CASA_ACCOUNT,
          );
        }

        break;

      case onboardingConstants.REGISTRATION_OPTION_CREDIT_CARD:
        props?.changeFlowStep(
          onboardingConstants.REGISTRATION_OPTION_CREDIT_CARD,
        );
        break;

      default:
        break;
    }
  };

  useEffect(() => {
    if (
      Array.isArray(userNameValidateData?.suggestedUserNames) &&
      userNameValidateData?.suggestedUserNames?.length > 0
    ) {
      setHasNameSuggestions(true);
      setIUserNameValid(false);
      setCommonErrorMsg(t("This username is not available."));
    } else if (
      userNameValidateData?.responseCode == responseCodes.SUCCESS_CODE
    ) {
      setHasNameSuggestions(false);
      setIUserNameValid(true);

      let payload = {
        username: validUsername,
      };

      dispatch(storeProfileWizardDetails(payload));
      dispatch(clearValidateUsernameData());
      dispatch(clearCommonErrors());
      dispatch(removeFormDataFeilds(["password"]));
      props?.clearEphemeralPassword?.();
      props?.changeFlowStep(onboardingConstants.ONBOARDING_PASSWORD_STEP);
    } else {
      setHasNameSuggestions(false);
      setIUserNameValid(!!ownFormData?.username);
    }
  }, [userNameValidateData]);

  const assignSelectedUsername = (name) => {
    setSelectedUsername(name);
    setValidUsername("");
    setIUserNameValid(true);
  };

  const submit = (values) => {
    setValidUsername(values.username);
    dispatch(
      onboardingUsernameValidateRequest({
        username: values.username,
        locale: i18n.language,
      }),
    );
  };

  return (
    <>
      <div className="df-user-profile df-reset-cd-form df-onboard-progress-back-close df-profile-top-aligned-box">
        <Container maxWidth="md" className="df-pos-rel">
          <div
            className="df-user-profile__close-icon"
            onClick={props?.closeBtn}
          >
            <CloseIcon />
          </div>

          <div className="df-user-profile__back-icon df-bc-w-progress">
            <ProgressBar
              noOfActive={4}
              total={
                ownFormData?.registrationType ==
                onboardingConstants.REGISTRATION_OPTION_CASA_ACCOUNT
                  ? 7
                  : 6
              }
            />

            <BackIcon onClick={goBack} />
          </div>

          <div className="df-user-profile__body df-pw-tab df-card-details-tb">
            <Grid container>
              <Grid size={12} className="text-left df-pl-2">
                <h2 className="df-reset-pw-title">
                  {t("Enter a username for your digital banking account")}
                </h2>

                <Form
                  onSubmit={submit}
                  initialValues={{ username: selectedUsername }}
                  validate={onValidate}
                  render={({
                    handleSubmit,
                    values,
                    hasValidationErrors,
                    form,
                  }) => {
                    const isFormValid = !hasValidationErrors;

                    const { dirty } = form.getState();

                    useEffect(() => {
                      form.change("isUserNameValid", isUserNameValid);
                    }, [isUserNameValid, form]);

                    useEffect(() => {
                      form.change("username", selectedUsername);
                    }, [selectedUsername]);

                    return (
                      <>
                        <div className="df-onboard-inputs">
                          <UiInput
                            name="username"
                            id="username"
                            label={t("Username") + " *"}
                            type="text"
                            min={0}
                            value={values?.username}
                            inputonChange={(e) => {
                              setIUserNameValid(false);
                              setCommonErrorMsg("");
                              const input = e.target.value;
                              const cleaned = input
                                .replace(/[^a-zA-Z0-9]/g, "")
                                .replace(/^[0-9]+/, "");

                              form.change("username", cleaned);
                              if (e.target.value == "") {
                                setSelectedUsername("");
                              }
                            }}
                            clearable={true}
                            minLength={3}
                            maxLength={20}
                            form={form}
                            helperText={
                              !isUserNameValid && isFormValid
                                ? commonErrorMsg
                                : t(
                                    "Must be at least 6 characters, including letters and at least 4 numbers.",
                                  )
                            }
                            inputIcon={
                              isUserNameValid ? (
                                <TickGreenIcon />
                              ) : (
                                values?.username.length > 0 && <ClearIcon />
                              )
                            }
                            endAdornmentOnClick={() => {
                              form.change("username", "");
                            }}
                            error={
                              !isUserNameValid && commonErrorMsg != ""
                                ? true
                                : null
                            }
                          />
                        </div>

                        {hasNameSuggestions ? (
                          <div className="df-onboard-username-radio">
                            <p>{t("Here’s what we suggest")}</p>

                            <div className="df-onboard-radio">
                              <UiRadioInput
                                className="df-custom-radio"
                                formcontrolclass="w-100"
                                defaultValue={values.username}
                                key={values.username}
                                name="accounts-seclect-group"
                              >
                                {Array.isArray(
                                  userNameValidateData?.suggestedUserNames,
                                ) &&
                                  userNameValidateData?.suggestedUserNames.map(
                                    (suggestion) => {
                                      return (
                                        <>
                                          <FormControlLabel
                                            value={suggestion}
                                            control={<Radio />}
                                            onClick={() => {
                                              form.change(suggestion);
                                              assignSelectedUsername(
                                                suggestion,
                                              );
                                            }}
                                            label={
                                              <div className="df-radio-img-label">
                                                <span>{suggestion}</span>
                                              </div>
                                            }
                                          />
                                        </>
                                      );
                                    },
                                  )}
                              </UiRadioInput>
                            </div>
                          </div>
                        ) : null}

                        <div
                          className={`df-onboard-success-wr ${
                            !hasNameSuggestions
                              ? "df-onboard-username-alert-space"
                              : ""
                          }`}
                        >
                          {/* Not in LOLC  */}
                          {/* <div className="df-onboard-username ">
                            <AlertIcon />
                            <p>
                              {t(
                                "Using your personal information such as your name or email address for the username is not advisable.",
                              )}
                            </p>
                          </div> */}
                        </div>

                        <UiButton
                          onClick={handleSubmit}
                          className="df-ui-dark-bg-btn w-100 mt-2"
                          name={t("Next")}
                          disabled={!isFormValid}
                        />
                      </>
                    );
                  }}
                />
              </Grid>
            </Grid>
          </div>
        </Container>
      </div>
    </>
  );
};
